import { CircularProgress, Stack, useTheme } from "@mui/material";
import { useNavigate } from "react-router-dom";

import type {
  ExtendedVisionUser,
  GetIncidentWorkItemResponse,
} from "@thalamos/vision-common";
import { isCurrentUserSupervisor } from "@thalamos/vision-common";

import { apiHooks } from "../../api/index.js";
import type { IncidentFormType } from "../../components/IncidentWorkItems/StartNewIncidentWorkItem/StartNewIncidentWorkItem.js";
import { StartNewIncidentWorkItem } from "../../components/IncidentWorkItems/StartNewIncidentWorkItem/StartNewIncidentWorkItem.js";
import { useUser } from "../../Context/UserContext.js";
import { useQuickCreate136 } from "../../hooks/useQuickCreate136.js";
import { QuickCreateDemoIncident } from "../../modals/QuickCreateDemoIncident/QuickCreateDemoIncident.js";
import { createRoute } from "../../routing/createRoute.js";
import { AdminPage } from "../AdminPage/AdminPage.js";
import { Error } from "../Error/Error.js";
import SupervisorDashboardPage from "../SupervisorDashboardPage/SupervisorDashboardPage.js";
import { HomePageIncidents } from "./HomePageIncidents.js";

export const HomePageInner = ({
  user,
  incidentWorkItems,
  refetchWorkItems,
}: {
  user: ExtendedVisionUser | undefined;
  incidentWorkItems: GetIncidentWorkItemResponse | undefined;
  refetchWorkItems: ReturnType<
    typeof apiHooks.incidentWorkItem.getMyActiveIncidentWorkItems
  >[1];
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  useQuickCreate136();
  const isMentalCapacityActEnabled =
    user?.sessionOrganisationConfiguration?.mentalCapacityActEnabled;

  const enabledForms: IncidentFormType[] = [
    "section136",
    ...(isMentalCapacityActEnabled ? ["mca" as IncidentFormType] : []),
    // "voluntary",
  ];

  return (
    <Stack
      data-testid="home"
      sx={{
        [theme.breakpoints.up("sm")]: {
          padding: "2rem",
        },
        [theme.breakpoints.down("sm")]: {
          padding: "1rem",
        },
        width: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <StartNewIncidentWorkItem
        enabledForms={enabledForms}
        onNewForm={() => {
          navigate(createRoute.incidentCreate());
        }}
      />

      <HomePageIncidents
        user={user}
        incidentWorkItems={incidentWorkItems}
        refetchWorkItems={refetchWorkItems}
      />

      <QuickCreateDemoIncident />
    </Stack>
  );
};

const HomePage = () => {
  const { user, loading: loadingUser, error: userError } = useUser();

  const [
    {
      data: incidentWorkItems,
      loading: loadingIncidentWorkItems,
      error: incidentWorkItemsError,
    },
    refetchWorkItems,
  ] = apiHooks.incidentWorkItem.getMyActiveIncidentWorkItems(user?.id);

  if (loadingUser || loadingIncidentWorkItems) {
    return <CircularProgress />;
  }

  if (userError || incidentWorkItemsError) {
    return <Error />;
  }

  const render = ({
    incidentWorkItems,
    user,
    refetchWorkItems,
  }: {
    incidentWorkItems: GetIncidentWorkItemResponse | undefined;
    user: ExtendedVisionUser | undefined;
    refetchWorkItems: ReturnType<
      typeof apiHooks.incidentWorkItem.getMyActiveIncidentWorkItems
    >[1];
  }) => {
    const isSupervisorUser = user ? isCurrentUserSupervisor(user) : false;
    const isAdminUser = user?.isAdminSession;
    return isAdminUser ? (
      <AdminPage user={user} />
    ) : isSupervisorUser ? (
      <SupervisorDashboardPage />
    ) : (
      <HomePageInner
        user={user}
        incidentWorkItems={incidentWorkItems}
        refetchWorkItems={refetchWorkItems}
      />
    );
  };

  return render({
    user,
    incidentWorkItems,
    refetchWorkItems,
  });
};

export default HomePage;
